import React from 'react';
import { Form, Button, Card, Col, Space, Modal, ConfigProvider } from 'antd';
import { CheckCircleOutlined, FilePdfOutlined } from '@ant-design/icons';
import frFR from "antd/locale/fr_FR";
import Footer from '../../components/Footer';
import NavBar from '../../components/NavBar';
import SideBar from '../../components/SideBar';
import { SINISTER_COLORS } from '../../components/ui/colors';
import PreviewFicheDeclaration from './components/declaration/PreviewFicheDeclaration';
import DTRFFormat from '../production/components/docs/DTRFFormat';
import AttestationMoto from '../production/components/docs/AttestationMoto';
import AssureStep from './components/declaration/steps/AssureStep';
import AdversairesStep from './components/declaration/steps/AdversairesStep';
import AccidentStep from './components/declaration/steps/AccidentStep';
import VictimesStep from './components/declaration/steps/VictimesStep';
import useDeclarationSinistre from '../../domains/sinistres/hooks/useDeclarationSinistre';

const steps = [
    { title: 'Informations sur le véhicule assuré', subtitle: 'Ajouter les infos du conducteur', icon: 'bx bx-user' },
    { title: 'Infos sur la(les) partie(s) adverse(s)', subtitle: 'Saisir les infos de la partie adverse', icon: 'bx bx-user-pin' },
    { title: "Informations sur l'accident", subtitle: "Renseigner les infos sur l'accident", icon: 'bx bxs-car-crash' },
    { title: 'Informations sur la(les) victime(s)', subtitle: 'Renseigner les infos sur les victimes', icon: 'bx bx-user-voice' },
    { title: 'Résumé de la déclaration', subtitle: 'Vérifier les informations', icon: 'bx bx-detail' },
];

const DeclareSinistre = () => {
    const ctx = useDeclarationSinistre();

    const renderStep = () => {
        switch (ctx.currentStep) {
            case 0:
                return (
                    <AssureStep
                        contratsLoading={ctx.contratsLoading} contratsData={ctx.contratsData}
                        engins={ctx.engins} isAssureConducteur={ctx.isAssureConducteur}
                        setIsAssureConducteur={ctx.setIsAssureConducteur}
                        cityOptions={ctx.cityOptions} selectedContratObj={ctx.selectedContratObj}
                        onContratChange={ctx.handleContratChange}
                        onSearchContrats={ctx.searchContrats}
                        onDateChange={ctx.handleDateSurvenanceChange}
                        form={ctx.form}
                    />
                );
            case 1:
                return (
                    <AdversairesStep
                        adversaires={ctx.adversaires} setAdversaires={ctx.setAdversaires}
                        adversairePrincipalId={ctx.adversairePrincipalId}
                        setAdversairePrincipalId={ctx.setAdversairePrincipalId}
                        assuranceOptions={ctx.assuranceOptions}
                        cityOptions={ctx.cityOptions}
                    />
                );
            case 2:
                return (
                    <AccidentStep
                        entites={ctx.entites} quartiersData={ctx.quartiersData}
                        communesData={ctx.communesData} loadingQuartiers={ctx.loadingQuartiers}
                        selectedLocation={ctx.selectedLocation}
                        setSelectedLocation={() => {}} // managed internally via handlers
                        typeSinistre={ctx.typeSinistre} setTypeSinistre={ctx.setTypeSinistre}
                        formData={ctx.formData} adversaires={ctx.adversaires}
                        adversairePrincipalId={ctx.adversairePrincipalId}
                        onQuartierSelect={ctx.handleQuartierSelect}
                        onCommuneSelect={ctx.handleCommuneSelect}
                        form={ctx.form}
                    />
                );
            case 3:
                return (
                    <VictimesStep
                        typeSinistre={ctx.typeSinistre} garanties={ctx.garanties}
                        evaluationInitiale={ctx.evaluationInitiale}
                        setEvaluationInitiale={ctx.setEvaluationInitiale}
                        victimes={ctx.victimes} setVictimes={ctx.setVictimes}
                        autoVictimes={ctx.autoVictimes} professions={ctx.professions}
                        selectedContratObj={ctx.selectedContratObj} adversaires={ctx.adversaires}
                        selectedGaranties={ctx.selectedGaranties}
                        setSelectedGaranties={ctx.setSelectedGaranties}
                        calculateEvaluation={ctx.calculateEvaluation}
                    />
                );
            case 4:
                return (
                    <PreviewFicheDeclaration
                        sequenceSinistre={ctx.sequenceSinistre}
                        selectedLocation={ctx.selectedLocation}
                        isAssureConducteur={ctx.isAssureConducteur}
                        sinistre={ctx.formData}
                        adversaires={ctx.adversaires.map(a => ({ ...a, estPrincipal: a.id === ctx.adversairePrincipalId }))}
                        garanties={ctx.selectedGaranties}
                        victimes={[...ctx.autoVictimes, ...ctx.victimes]}
                        contrat={ctx.selectedContratObj}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu="SINISTRES" />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ padding: 24, minHeight: '100vh' }}>
                                <link href="https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css" rel="stylesheet" />
                                <style>{`
                                    .bs-stepper-header{display:flex;flex-direction:column;gap:0}
                                    .step{display:flex;flex-direction:column;align-items:stretch}
                                    .step-trigger{display:flex;align-items:center;gap:4px;background:none;border:none;padding:3px 3px 3px 12px;width:100%;text-align:left;cursor:pointer;border-radius:8px;transition:all .3s;position:relative}
                                    .step-trigger:hover{background:#f8f9fa}
                                    .step.active .step-trigger{background:${SINISTER_COLORS.primary}}
                                    .step.active .step-trigger::before{content:'';position:absolute;left:0;top:0;bottom:0;width:4px;background:#fff;border-radius:4px}
                                    .step.active .bs-stepper-circle{background:#fff;color:#667eea}
                                    .step.active .bs-stepper-title{color:#fff;font-weight:700}
                                    .step.active .bs-stepper-subtitle{color:rgba(255,255,255,.9)}
                                    .bs-stepper-circle{width:36px;height:36px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:#e9ecef;color:#6c757d;font-size:18px;transition:all .3s;flex-shrink:0}
                                    .bs-stepper-label{display:flex;flex-direction:column;gap:1px;flex:1}
                                    .bs-stepper-title{font-size:14px;font-weight:600;color:#333;line-height:1.3}
                                    .bs-stepper-subtitle{font-size:11px;color:#6c757d;line-height:1.3}
                                    .line{width:2px;height:12px;background:#e9ecef;margin-left:29px}
                                    .card{background:#fff;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,.08);padding:24px}
                                `}</style>

                                <ConfigProvider locale={frFR}>
                                    <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', gap: 24 }}>
                                        {/* Sidebar Steps */}
                                        <div style={{ width: 280, flexShrink: 0 }}>
                                            <div className="card" style={{ position: 'sticky', top: 24, padding: 16 }}>
                                                <div className="bs-stepper-header">
                                                    {steps.map((step, index) => (
                                                        <React.Fragment key={index}>
                                                            <div className={ctx.currentStep === index ? 'step active' : 'step'}>
                                                                <button type="button" className="step-trigger" onClick={() => ctx.setCurrentStep(index)}>
                                                                    <span className="bs-stepper-circle"><i className={step.icon} /></span>
                                                                    <span className="bs-stepper-label">
                                                                        <span className="bs-stepper-title">{step.title}</span>
                                                                        <span className="bs-stepper-subtitle">{step.subtitle}</span>
                                                                    </span>
                                                                </button>
                                                            </div>
                                                            {index < steps.length - 1 && <div className="line" />}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                                <div className="text-center" style={{ marginTop: 40 }}>
                                                    <Button type="primary" icon={<FilePdfOutlined />} onClick={ctx.handleContractPreview}
                                                        style={{ backgroundColor: 'gray' }} disabled={!ctx.selectedContratObj}>
                                                        Prévisualiser le contrat
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contenu principal */}
                                        <Col xs={24} md={18}>
                                            <Form form={ctx.form} layout="vertical">
                                                {renderStep()}
                                            </Form>

                                            <Card style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', marginTop: 24 }}>
                                                <div style={{ textAlign: 'right' }}>
                                                    <Space size="large">
                                                        {ctx.currentStep > 0 && (
                                                            <Button size="large" onClick={ctx.prev} style={{ minWidth: 120 }}>← Précédent</Button>
                                                        )}
                                                        {ctx.currentStep < steps.length - 1 && (
                                                            <Button type="primary" size="large" onClick={ctx.next}
                                                                style={{ background: SINISTER_COLORS.primary, border: 'none', minWidth: 120 }}>
                                                                Suivant →
                                                            </Button>
                                                        )}
                                                        {ctx.currentStep === steps.length - 1 && (
                                                            <Button type="primary" size="large" onClick={ctx.handleSubmit}
                                                                icon={<CheckCircleOutlined />} loading={ctx.loading}
                                                                style={{ background: SINISTER_COLORS.primary, border: 'none', minWidth: 180 }}>
                                                                Enregistrer la Déclaration
                                                            </Button>
                                                        )}
                                                    </Space>
                                                </div>
                                            </Card>
                                        </Col>
                                    </div>
                                </ConfigProvider>

                                <Modal title="Condition Particulière & Attestation" centered
                                    open={ctx.cModal} onOk={() => ctx.setCModal(false)} onCancel={() => ctx.setCModal(false)}
                                    width={1200} zIndex={2000}>
                                    <div id="myContrat">
                                        <div style={{ width: '210mm', height: '297mm' }}><DTRFFormat contrat={ctx.theContract} /></div>
                                        <div style={{ width: '297mm', height: '210mm' }}><AttestationMoto engin={ctx.theContract?.engins?.[0]} contrat={ctx.theContract} /></div>
                                    </div>
                                </Modal>
                            </div>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade" />
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle" />
            <div className="drag-target" />
        </div>
    );
};

export default DeclareSinistre;
